import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, Trash2, Paperclip, AlertCircle } from 'lucide-react'
import { TaskAttachment, attachmentService } from '../services/attachment-service'
import { FileUpload } from './FileUpload'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { LoadingSpinner } from './LoadingSpinner'

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
interface TaskAttachmentsProps {
  taskId: string
  canUpload?: boolean
  canDelete?: boolean
  className?: string
}

export function TaskAttachments({ 
  taskId, 
  canUpload = true, 
  canDelete = true,
  className = ""
}: TaskAttachmentsProps) {
  const [attachments, setAttachments] = useState<TaskAttachment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set())
  const requestSeq = useRef(0)
  const taskIdRef = useRef(taskId)

  useEffect(() => {
    taskIdRef.current = taskId
  }, [taskId])

  const loadAttachments = useCallback(async () => {
    const seq = ++requestSeq.current

    try {
      setLoading(true)
      setError(null)
      const data = await attachmentService.getTaskAttachments(taskId)

      if (requestSeq.current === seq) {
        setAttachments(data)
      }
    } catch (err: unknown) {
      if (requestSeq.current === seq) {
        setError(getErrorMessage(err, 'Erro ao carregar anexos'))
      }
    } finally {
      if (requestSeq.current === seq) {
        setLoading(false)
      }
    }
  }, [taskId])

  useEffect(() => {
    loadAttachments()

    return () => {
      requestSeq.current += 1
    }
  }, [loadAttachments])

  const handleUpload = async (files: FileList) => {
    const uploadTaskId = taskId

    try {
      setError(null)
      
      if (files.length === 1) {
        const attachment = await attachmentService.uploadTaskAttachment(uploadTaskId, files[0])
        if (taskIdRef.current !== uploadTaskId) return
        setAttachments(prev => [attachment, ...prev])
      } else {
        const result = await attachmentService.uploadMultipleTaskAttachments(uploadTaskId, files)
        if (taskIdRef.current !== uploadTaskId) return

        if (result.attachments) {
          setAttachments(prev => [...result.attachments!, ...prev])
        }
        if (result.errors && result.errors.length > 0) {
          setError(`Alguns arquivos falharam: ${result.errors.join(', ')}`)
        }
      }
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Erro ao enviar arquivo(s)'))
    }
  }

  const handleDownload = async (attachment: TaskAttachment) => {
    if (downloadingIds.has(attachment.id)) {
      return
    }

    try {
      setDownloadingIds(prev => new Set(prev).add(attachment.id))
      setError(null)
      await attachmentService.downloadAndSaveFile(attachment.id, attachment.name)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao baixar arquivo'))
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev)
        next.delete(attachment.id)
        return next
      })
    }
  }

  const handleDelete = async (attachmentId: number) => {
    if (deletingIds.has(attachmentId)) {
      return
    }

    if (!window.confirm('Tem certeza que deseja excluir este anexo?')) {
      return
    }

    try {
      setDeletingIds(prev => new Set(prev).add(attachmentId))
      setError(null)
      await attachmentService.deleteAttachment(attachmentId)
      setAttachments(prev => prev.filter(att => att.id !== attachmentId))
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao excluir anexo'))
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev)
        next.delete(attachmentId)
        return next
      })
    }
  }

  const formatDate = (dateString: string) => {
    const date = parseDbTimestamp(dateString)
    if (!date) return '-'
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground flex items-center">
          <Paperclip className="mr-2 h-5 w-5" />
          Anexos ({attachments.length})
        </h3>
      </div>

      {/* Error Message */}
      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-center">
            <AlertCircle className="mr-2 size-5 shrink-0 text-destructive" aria-hidden="true" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      {/* Upload Area */}
      {canUpload && (
        <FileUpload
          onUpload={handleUpload}
          multiple={true}
          maxFiles={5}
          maxSize={10}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
        />
      )}

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <Card className="p-8 text-center">
          <Paperclip className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="text-muted-foreground">Nenhum anexo encontrado</p>
          {canUpload && (
            <p className="text-sm text-muted-foreground mt-2">
              Use a área acima para enviar arquivos
            </p>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {attachments.map((attachment) => (
            <Card key={attachment.id} className="p-4">
              <div className="space-y-3">
                {/* File Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="text-2xl">
                      {attachmentService.getFileIcon(attachment.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground truncate" title={attachment.name}>
                        {attachment.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {attachmentService.formatFileSize(attachment.size)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* File Info */}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Enviado por:</span> {attachment.uploaderName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Data:</span> {formatDate(attachment.uploadedAt)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(attachment)}
                    disabled={downloadingIds.has(attachment.id)}
                    className="flex-1"
                  >
                    {downloadingIds.has(attachment.id) ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Download className="h-3 w-3 mr-1" />
                    )}
                    Baixar
                  </Button>
                  
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(attachment.id)}
                      disabled={deletingIds.has(attachment.id)}
                      aria-label="Excluir anexo"
                    >
                      {deletingIds.has(attachment.id) ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>

              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
